import type { BlockchainMeta } from '../../../api/models/MetaModels';
import type { DeprecatedWalletDetail } from '../../../types/wallet';
import type { Blockchain, Wallet, WalletType } from '../../Constants';
import type { WalletInfo } from '@rango-dev/widget-embedded';

import {
  BLOCKCHAINS_THAT_SUPPORT_PROFILE,
  WALLETS_WITHOUT_SIGN_MESSAGE_METHOD,
} from '@rango-dev/internal-profile/constants';
import { allProviders as getAllProviders, WalletTypes } from '@rango-dev/provider-all';
import { readAccountAddress } from '@rango-dev/widget-embedded';
import BigNumber from 'bignumber.js';
import { ethers } from 'ethers';

import { hasLedgerWalletCredentials } from '../../../constants/wallets';
import { Networks, ZERO } from '../../Constants';

interface PartialEventInfo {
  isContractWallet: boolean;
  namespace?: string;
}

export function prepareAccountsForRedux(
  wallet: WalletType,
  accounts: string[],
  connectedNetwork: Networks | null,
  evmBasedChains: string[],
  supportedChainNames: Networks[] | null,
  partialInfo: PartialEventInfo,
): Blockchain[] {
  const result = {} as { [type in Networks]: Blockchain };

  function addAccount(network: Networks, address: string) {
    const isConnected = network === connectedNetwork;
    const newAccount = {
      address,
      balances: null,
      loading: true,
      walletType: wallet,
      isConnected,
      error: false,
      explorerUrl: null,
    };

    if (!!result[network]) {
      result[network].accounts.push(newAccount);
    } else {
      result[network] = {
        name: network,
        accounts: [newAccount],
        namespace: partialInfo.namespace,
      };
    }
  }

  const supportedChains = supportedChainNames || [];

  accounts.forEach((account) => {
    const { address, network } = readAccountAddress(account);

    const hasLimitation = supportedChains.length > 0;
    const isSupported = supportedChains.includes(network as Networks);
    const isUnknown = network === Networks.Unknown;
    const notSupportedNetworkByWallet = hasLimitation && !isSupported && !isUnknown;

    /*
     * Here we check given `network` is not supported by wallet
     * And also the network is known.
     */
    if (notSupportedNetworkByWallet) {
      return;
    }

    /*
     * In some cases we can handle unknown network by checking its address
     * pattern and act on it.
     * Example: showing our evm compatible netwrok when the uknown network is evem.
     * Otherwise, we stop executing this function.
     */
    const isUknownAndEvmBased = network === Networks.Unknown && ethers.isAddress(address);
    if (isUnknown && !isUknownAndEvmBased) {
      return;
    }

    const isEvmBasedChain = evmBasedChains.includes(network);

    // If it's an evm network, we will add the address to all the evm chains.
    if (isEvmBasedChain || isUknownAndEvmBased) {
      if (partialInfo.isContractWallet) {
        addAccount(network as Networks, address.toLowerCase());
        return;
      }

      /*
       * all evm chains are not supported in wallets, so we are adding
       * only to those that are supported by wallet.
       */
      const evmChainsSupportedByWallet = supportedChains.filter((chain) =>
        evmBasedChains.includes(chain),
      );

      evmChainsSupportedByWallet.forEach((network) => {
        /*
         * EVM addresses are not case sensetive.
         * Some wallets like Binance-chain return some letters in uppercase which produces bugs in our wallet state.
         */
        addAccount(network, address.toLowerCase());
      });
    } else {
      addAccount(network as Networks, address);
    }
  });

  return Object.values(result);
}

export { walletAndSupportedChainsNames } from '@rango-dev/internal-profile/utils';

export const calculateWalletUsdValue = (
  wallet: Wallet | null,
  selectedBlockchain: Networks[],
  selectedWallet: string[],
): number => {
  const uniqueAccountAddresses = new Set<string>();
  const modifiedWalletBlockchains = wallet?.blockchains?.map((blockchain) => {
    const modifiedWalletBlockchain: Blockchain = { name: blockchain.name, accounts: [] };
    blockchain.accounts.forEach((account) => {
      if (!uniqueAccountAddresses.has(account.address)) {
        uniqueAccountAddresses.add(account.address);
      }
    });
    uniqueAccountAddresses.forEach((accountAddress) => {
      const lastConnectedAccountWithSameAddress = [...blockchain.accounts]
        .reverse()
        .find((b) => b.address === accountAddress);

      if (
        !!lastConnectedAccountWithSameAddress &&
        selectedWallet.includes(lastConnectedAccountWithSameAddress.walletType)
      ) {
        modifiedWalletBlockchain.accounts.push(lastConnectedAccountWithSameAddress);
      }
    });
    return modifiedWalletBlockchain;
  });

  const filteredModifiedWalletBlockchains = modifiedWalletBlockchains?.filter((blockchain) =>
    selectedBlockchain.includes(blockchain.name as Networks),
  );

  const usdValue =
    filteredModifiedWalletBlockchains
      ?.flatMap((b) => b.accounts)
      ?.flatMap((a) => a?.balances)
      ?.map((b) => new BigNumber(b?.amount || ZERO).multipliedBy(b?.usdPrice || 0))
      ?.reduce((a, b) => a.plus(b), ZERO) || ZERO;
  return usdValue.toNumber();
};

export const hasSomeLoadingAccount = (wallet: Wallet | null): boolean =>
  (wallet?.blockchains?.filter((b) =>
    b.accounts.flatMap((it) => it.loading).reduce((x, y) => x || y),
  )?.length || 0) > 0;

export const sortWalletDetailsByChains = (
  walletsDetails: DeprecatedWalletDetail[],
  sortedBlockchains: BlockchainMeta[],
) => {
  const sortedBlockchainsNames = sortedBlockchains.map((blockchain) => blockchain.name);

  return walletsDetails
    .slice()
    .sort(
      (a, b) => sortedBlockchainsNames.indexOf(a.chain) - sortedBlockchainsNames.indexOf(b.chain),
    );
};

export const getProviders = () => {
  const providers = getAllProviders();
  const builtProviders = providers.map((build) => build());

  /*
   * The ledger wallet provider throws on init when its credentials are missing,
   * so it is left out entirely unless both env vars are configured.
   */
  return builtProviders.filter(
    (provider) => provider.id !== WalletTypes.LEDGER_WALLET || hasLedgerWalletCredentials,
  );
};

export function shouldExcludeWalletInProfileWalletsList(
  wallet: WalletInfo | Pick<WalletInfo, 'supportedChains'>,
) {
  if ('name' in wallet && WALLETS_WITHOUT_SIGN_MESSAGE_METHOD.includes(wallet.name)) {
    return true;
  }
  if (
    !BLOCKCHAINS_THAT_SUPPORT_PROFILE.some((blockchain) =>
      wallet.supportedChains.map((chain) => chain.name).includes(blockchain),
    )
  ) {
    return true;
  }
  return false;
}
